/**
 * SuspendConfirmDialog (Sprint 5 S5-B-components)
 *
 * Confirmation dialog for the FI Staff suspend action. Composes the
 * `@aaimplatform/ui` `Dialog`, `Textarea`, and `Button` primitives with
 * zero local re-implementations.
 *
 * Sprint 2 invariant: a suspend takes effect immediately and revokes
 * active sessions. Copy below the form quantifies the propagation
 * window (60 seconds) so the operator understands the blast radius.
 *
 * Validation: the `reason` field is required and must be at least 10
 * characters. The submit button stays disabled until the value passes
 * the client-side check; the parent handler (`onConfirm`) is the
 * authoritative gate and will receive the trimmed reason string.
 *
 * A failed suspend renders inside the dialog (`errorMessage`, GH #2307):
 * the dialog stays open on failure, so an alert on the page behind it is
 * hidden by the modal.
 */

import { useEffect, useState } from 'react';
import { Alert, Button, Dialog, Textarea } from '@aaimplatform/ui';

const MIN_REASON_LENGTH = 10;

export interface SuspendConfirmDialogProps {
  /** Controls dialog visibility. Mirrors React Aria modal contract. */
  open: boolean;
  /** Visibility change callback (close button, Escape key, backdrop). */
  onOpenChange: (open: boolean) => void;
  /** Display name for the staff member being suspended. */
  staffName: string;
  /** Email of the staff member; reserved for future copy variants. */
  staffEmail: string;
  /**
   * Submit handler. Receives the trimmed reason string. The parent owns
   * the network call (`useSuspendStaff`) and the step-up token plumbing.
   */
  onConfirm: (reason: string) => Promise<void>;
  /** Disables the submit button while the parent mutation is in flight. */
  isPending?: boolean;
  /**
   * The parent mutation's failure, rendered inside the dialog. The parent
   * clears it (`mutation.reset()`) when the dialog closes.
   */
  errorMessage?: string;
}

/**
 * SuspendConfirmDialog -- Sprint 5 confirmation surface for the FI Staff
 * suspend action. Pure UI; the parent wires the mutation and step-up.
 */
export function SuspendConfirmDialog({
  open,
  onOpenChange,
  staffName,
  staffEmail: _staffEmail,
  onConfirm,
  isPending = false,
  errorMessage,
}: SuspendConfirmDialogProps) {
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  // Reset transient state whenever the dialog closes so a re-open starts
  // from a clean slate. This avoids leaking the prior staff member's
  // reason into the next confirmation.
  useEffect(() => {
    if (!open) {
      setReason('');
      setTouched(false);
    }
  }, [open]);

  const trimmed = reason.trim();
  const isValid = trimmed.length >= MIN_REASON_LENGTH;
  const showError = touched && !isValid;

  const handleSubmit = async () => {
    setTouched(true);
    if (!isValid || isPending) return;
    await onConfirm(trimmed);
  };

  const handleCancel = () => {
    if (isPending) return;
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={`Suspend ${staffName}`}
      size="md"
      isDismissable={!isPending}
      isKeyboardDismissDisabled={isPending}
      footer={
        <>
          <Button
            variant="tertiary"
            onClick={handleCancel}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              void handleSubmit();
            }}
            disabled={!isValid || isPending}
          >
            {isPending ? 'Suspending...' : `Suspend ${staffName}`}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {errorMessage && (
          <Alert type="error" title="Suspend failed">
            {errorMessage}
          </Alert>
        )}
        <Textarea
          label="Reason"
          value={reason}
          onChange={(value) => {
            setReason(value);
            if (!touched) setTouched(true);
          }}
          onBlur={() => setTouched(true)}
          required
          rows={4}
          maxLength={500}
          showCharCount
          fullWidth
          helperText={`Minimum ${MIN_REASON_LENGTH} characters. Recorded on the staff member's audit trail.`}
          error={showError ? `Provide at least ${MIN_REASON_LENGTH} characters.` : undefined}
          placeholder="Explain the operational reason for this suspension."
        />
        <p className="text-sm font-sans text-secondary">
          Active sessions are revoked immediately. The user will be signed
          out within 60 seconds.
        </p>
      </div>
    </Dialog>
  );
}

export default SuspendConfirmDialog;
